import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Radar, Users, TrendingUp, Palette, Megaphone, ArrowRight, Info, Sparkles } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-6 card-hover" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
      <div className="w-9 h-9 rounded-lg bg-navy/5 flex items-center justify-center"><Icon className="w-4 h-4 text-navy" /></div>
    </div>
    <div className="font-serif text-3xl font-bold text-navy">{value ?? "-"}</div>
    {sub && <div className="text-sm text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function BuyerIntelligence() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/intelligence/dashboard").then(({ data }) => setDash(data)).catch(() => {}),
      api.get("/intelligence/personas").then(({ data }) => setPersonas(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const activePersonas = personas.filter((p) => p.active);

  return (
    <div className="space-y-10 fade-up" data-testid="buyer-intelligence-page">
      <div className="relative overflow-hidden rounded-3xl navy-gradient p-8 sm:p-10">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-4">
            <Radar className="w-4 h-4 text-gold" />
            <span className="text-xs uppercase tracking-widest text-gold font-semibold">Marine Buyer Intelligence</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="bi-title">
            Who is most likely to buy your boats
          </h1>
          <p className="text-slate-300">
            Marine Ad AI identifies patterns of boating intent and buyer behavior from your own inventory,
            campaigns and outcomes. These are likely personas and predicted intent, not private personal characteristics.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading buyer intelligence...</div>
      ) : dash?.not_enough_data ? (
        <div className="bg-white rounded-2xl border border-dashed border-gold/50 p-10 text-center" data-testid="bi-not-enough-data">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-gold/70" />
          <h2 className="font-serif text-xl font-bold text-navy mb-2">Not enough data yet</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            As you add inventory, create and publish campaigns, and capture leads and sales, Marine Ad AI
            will learn which buyer personas your activity attracts. We show real patterns only, never invented numbers.
          </p>
          <div className="text-sm text-muted-foreground mb-6">
            Signals captured so far: <span className="font-semibold text-navy">{dash?.total_signals ?? 0}</span>
            {" "}(need at least {dash?.min_signals ?? 3})
          </div>
          <Button onClick={() => navigate("/create-campaign")} className="gold-gradient text-navy font-bold" data-testid="bi-create-campaign">
            <Megaphone className="w-4 h-4 mr-2" /> Create a Campaign
          </Button>
        </div>
      ) : (
        <>
          <section data-testid="bi-stats">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <StatCard icon={Users} label="Active Personas" value={dash?.top_personas?.length || 0} sub={`over last ${dash?.window_days || 90} days`} testid="bi-stat-personas" />
              <StatCard icon={TrendingUp} label="Avg Purchase Intent" value={dash?.average_intent != null ? `${dash.average_intent}/100` : "Not enough data yet"} sub="across active buyers" testid="bi-stat-intent" />
              <StatCard icon={Palette} label="Top Creative Theme" value={dash?.best_theme_label || "Not enough data yet"} sub="most engaged" testid="bi-stat-theme" />
              <StatCard icon={Megaphone} label="Top Channel" value={dash?.best_channel || "Not enough data yet"} sub="most activity" testid="bi-stat-channel" />
            </div>
          </section>

          <section data-testid="bi-top-personas">
            <h2 className="font-serif text-2xl font-bold text-navy mb-5">Top Active Personas</h2>
            <div className="bg-white rounded-2xl border border-border divide-y">
              {dash?.top_personas?.length ? dash.top_personas.map((p) => (
                <button
                  key={p.persona_id}
                  onClick={() => navigate(`/buyer-intelligence/personas/${p.persona_id}`)}
                  data-testid={`bi-persona-row-${p.persona_id}`}
                  className="w-full flex items-center gap-4 p-4 text-left hover:bg-secondary transition-colors"
                >
                  <div className="w-40 shrink-0">
                    <div className="font-semibold text-navy truncate">{p.persona_name}</div>
                    <div className="text-xs text-muted-foreground">{p.active_prospects} prospect{p.active_prospects === 1 ? "" : "s"}</div>
                  </div>
                  <div className="flex-1 h-3 rounded-full bg-navy/10 overflow-hidden">
                    <div className="h-full gold-gradient" style={{ width: `${p.pct}%` }} />
                  </div>
                  <div className="w-12 text-right font-serif font-bold text-navy">{p.pct}%</div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              )) : (
                <div className="p-8 text-center text-muted-foreground">Not enough data yet.</div>
              )}
            </div>
          </section>
        </>
      )}

      <section data-testid="bi-persona-library">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-serif text-2xl font-bold text-navy">Your Marine Buyer Personas</h2>
            <p className="text-sm text-muted-foreground">Editable persona library used to interpret buyer signals. Tap a persona for detail.</p>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {activePersonas.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/buyer-intelligence/personas/${p.id}`)}
              data-testid={`bi-persona-card-${p.id}`}
              className="bg-white rounded-2xl border border-border p-5 text-left card-hover"
            >
              <div className="font-serif text-lg font-bold text-navy mb-1">{p.name}</div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{p.description}</p>
              <div className="flex flex-wrap gap-1.5">
                {(p.typical_categories || []).slice(0, 3).map((c) => (
                  <span key={c} className="text-[11px] font-semibold text-navy bg-navy/5 px-2 py-0.5 rounded-full">{c}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </section>

      <div className="flex items-start gap-3 bg-navy/5 rounded-xl p-4 text-sm text-muted-foreground" data-testid="bi-privacy-note">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-navy" />
        Marine Ad AI identifies patterns of boating intent and buyer behavior, not private personal
        characteristics. Recommendations are suggested strategies based on your first-party data and are not guarantees.
      </div>
    </div>
  );
}
